import { useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { MaskTitle, Reveal, SampleBadge, SectionLabel } from "./motion";
import { formatRange, nextRace, pad, raceStatus, sortedRaces } from "@/lib/race";
import { useLang } from "@/context/LangContext";
import { CircuitDialog } from "./CircuitDialog";

const STATUS = {
  next: "bg-trident text-white",
  done: "border border-white/15 text-zinc-400",
  upcoming: "border border-white/25 text-white",
};

export const Calendar = ({ races }) => {
  const list = sortedRaces(races);
  const next = nextRace(list);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(null);
  const { t, lang } = useLang();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 22 });
  const sy = useSpring(y, { stiffness: 180, damping: 22 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - r.left);
    y.set(e.clientY - r.top);
  };

  return (
    <section id="calendario" data-testid="calendar-section" className="relative bg-ink px-5 py-24 md:px-10 md:py-36">
      <div className="mx-auto max-w-[1600px]">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <SectionLabel index="03">{t.calendar.label}</SectionLabel>
            <MaskTitle key={lang} lines={[t.calendar.title[0], { text: t.calendar.title[1], className: "text-volt" }]}
              className="mt-6 font-display text-6xl font-black uppercase leading-[0.85] md:text-8xl lg:text-9xl" />
          </div>
          <SampleBadge show={list.some((r) => r?.sample)} />
        </div>

        <div className="relative mt-14" onMouseMove={onMove} onMouseLeave={() => setActive(null)} data-testid="calendar-list">
          <motion.div style={{ x: sx, y: sy }} animate={{ opacity: active != null ? 1 : 0, scale: active != null ? 1 : 0.85 }}
            transition={{ duration: 0.3 }} className="pointer-events-none absolute left-0 top-0 z-20 -ml-40 -mt-28 hidden h-56 w-80 overflow-hidden md:block">
            {active != null && <img src={list[active]?.image || "/images/ontrack.jpg"} alt="" className="h-full w-full object-cover" />}
          </motion.div>

          {list.length === 0 && <p className="py-10 text-zinc-400" data-testid="calendar-empty">{t.calendar.empty}</p>}
          {list.map((r, i) => {
            const key = raceStatus(r, next);
            return (
              <Reveal key={r?.id || i} y={24} delay={Math.min(i * 0.05, 0.3)}>
                <button type="button" data-testid={`race-row-${i}`} onMouseEnter={() => setActive(i)} onClick={() => setOpen(r)} aria-label={`${t.calendar.view} ${r?.circuit}`}
                  className={`group grid w-full grid-cols-12 items-center gap-3 border-t border-white/10 py-6 text-left md:py-9 ${raceStatus(r, next) === "done" ? "opacity-60 hover:opacity-100" : ""} transition-opacity`}>
                  <span className="col-span-2 flex flex-col gap-1 font-mono text-sm text-volt md:col-span-1">R{pad(r?.round ?? 0)}{r?.championship === "E4" && <span className="label-mono w-fit bg-white/10 px-1.5 py-0.5 text-[10px] text-white">E4</span>}</span>
                  <div className="col-span-10 flex items-center gap-4 md:col-span-6">
                    <img src={r?.image || "/images/ontrack.jpg"} alt="" className="h-14 w-20 shrink-0 object-cover md:hidden" />
                    <p className="font-display text-3xl font-black uppercase leading-[0.9] text-white transition-[transform,color] duration-500 group-hover:translate-x-4 group-hover:text-volt md:text-6xl lg:text-7xl">
                      {r?.circuit}
                    </p>
                  </div>
                  <div className="col-span-7 col-start-3 md:col-span-3 md:col-start-auto">
                    <p className="font-mono text-sm text-white">{formatRange(r?.date_start, r?.date_end, t.months)}</p>
                    <p className="font-mono text-xs text-zinc-400">{[r?.city, t.countries[r?.country] || r?.country].filter(Boolean).join(", ")}{r?.length_km ? ` · ${r.length_km} km` : ""}</p>
                  </div>
                  <span className="col-span-3 flex flex-col items-end gap-2 md:col-span-2">
                    <span className={`label-mono px-3 py-1.5 text-[11px] ${STATUS[key]}`} data-testid={`race-status-${i}`}>{t.calendar.status[key]}</span>
                    <span className="label-mono hidden text-[10px] text-white/70 transition-colors group-hover:text-white md:block">{t.calendar.view} →</span>
                  </span>
                </button>
              </Reveal>
            );
          })}
          <div className="border-t border-white/10" />
        </div>
      </div>
      <CircuitDialog race={open} onClose={() => setOpen(null)} />
    </section>
  );
};
